import { ControllerHelper, MediaDownloadDto, PlaylistController, PlaylistDownloadDto, PlayListNodeDownloadDto, PlaylistUploadDto, PostController } from 'collaboration-service';
import { PluginActions } from 'services/ApplicationState/PluginStateHandling';

import { Updater } from '@tanstack/react-table';
import CancelWithConfirmation from 'components/CancelWithConfirmation';
import MoreButton from 'components/MoreButton/MoreButton';
import EditPartContainer from 'components/PostEdit/EditPartContainer';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import SwitchButton from 'components/Switch/SwitchButton';
import TabsBar from 'components/TabsBar/TabsBar';
import { Button, Checkbox, Icon, Loader, LoaderInline, Tooltip, useBelowBreakpoint, useBelowBreakpointOrEqual, useThemePart } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { AnyAction } from 'redux';
import { Actions } from 'services/ApplicationState/Actions';
import { ApplicationState, useAppDispatch, useAppSelector, useFrontEndConfig } from 'services/ApplicationState/ApplicationState';
import { breakpoints } from 'services/ApplicationState/MasterLayout';
import { AccessHelpers, calcRolesToSet, removeHtmlTags, shallowCompare, useBreadCrumbs, visit } from 'services/Helpers';
import { may, mayDelete, mayUpdate } from 'services/Helpers/LinkHelpers';
import { createNamedRoute, gotoNamedRoute } from 'services/Helpers/RoutingHelper';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { RoleMap } from 'views/CMS/EditProject/CMSEditProject';
import { MissingElementAlternative, MissingElementAlternativeTitle } from 'views/CustomSC';
import PlaylistEditContent from 'views/Playlist/Edit/PlaylistEditContent';
import PlaylistEditGeneral from 'views/Playlist/Edit/PlaylistEditGeneral';
import { loadPostForPlaylist } from '../Playlist';
import { PlaylistSC } from '../PlaylistSC';
import PlaylistAIModal from './PlaylistAIModal';
import PlaylistEdit, { PlaylistEditActions, PlaylistEditState, useDispatchPLE, useSelectorPLE } from './PlaylistEdit';
import PlaylistEditAccess from './PlaylistEditAccess';
import PlaylistEditAutomation from './PlaylistEditAutomation';
import { ExpertSwitch, ExpertSwitchContainer, InputContainer } from './PlaylistEditClasses';
import PlaylistEditSequenceLinear from './PlaylistEditSequenceLinear';
import PlaylistEditTrainerContent from './PlaylistEditTrainerContent';
import useCalcMissingForPlaylistEdit from './useCalcMissingForPlaylistEdit';

export interface PlaylistUserRoles {
    assign: boolean;
    delete: boolean;
    update: boolean;
    create: boolean;
}

export const getPlaylistUserRoles = (playlist?: PlaylistDownloadDto): PlaylistUserRoles => {
    if (!playlist) {
        return { assign: false, delete: false, update: false, create: false };
    }
    return {
        assign: may(playlist, "assignPlaylist") || may(playlist, "unassignPlaylist"),
        delete: mayDelete(playlist),
        update: mayUpdate(playlist),
        create: may(playlist, "createContent")
    };
};

const mapper = (state: ApplicationState) => ({
    contentLanguage: state.contentLanguage,
    routeParams: state.params,
    missing: state.playlistsState.missing,
    user: state.user,
    globalConfig: state.features?.frontEndConfig ?? {}
});

export type PlaylistEditTab = "General" | "Content" | "Sequence" | "Automation" | "Access" | "TrainerContent";
const PlaylistEditTabs: PlaylistEditTab[] = ["General", "Content", "Sequence", "Automation", "Access", "TrainerContent"];
export interface PLaylistEditHomeProps {
}

const pleMapper = (state: PlaylistEditState) => ({
    expertMode: state.expertMode,
    playlist: state.playlist,
    originalPlaylist: state.originalPlaylist,
    missing: state.missing,
    posts: state.posts,
})





const PlaylistEditHomeInternal = (p: PLaylistEditHomeProps) => {

    const { t } = useImgI18N("playlist");
    const pleDispatch = useDispatchPLE();
    const { expertMode, playlist, missing, posts, originalPlaylist } = useSelectorPLE(pleMapper, shallowCompare)
    const { contentLanguage, routeParams, user, globalConfig } = useAppSelector(mapper, shallowCompare);
    const dispatch = useAppDispatch();
    const [selectedTab, setSelectedTab] = React.useState<PlaylistEditTab>("General");
    const config = useFrontEndConfig();
    const id = routeParams?.id as string;
    const isNewEntry = id === undefined;
    const copyPlaylist = routeParams?.copy !== undefined;
    const isEdit = !isNewEntry && !copyPlaylist;
    const [roles, setRoles] = React.useState<RoleMap>();
    const [loadedRoles, setLoadedRoles] = React.useState<RoleMap>();
    const enablePlaylistAIMode: boolean = globalConfig.enablePlaylistAIMode ?? false;

    React.useEffect(() => {
        pleDispatch(PlaylistEditActions.setExpertMode(false));
    }, [pleDispatch, id]);

    useBreadCrumbs({
        title: t("manage playlists"),
        route: createNamedRoute("playlist_manage")!,
        children: [],
    }, {
        title: t("edit playlists"),
        route: createNamedRoute("playlist_edit", { id })!,
        children: [],
    });


    const [saveRoutineSingle, setSaveRoutineSingle] = React.useState<boolean>(true);
    const isGlobalAdmin = user && AccessHelpers.isGlobalAdmin()(user) && user.email.endsWith("@imaginarity.com");
    const contentWidth = useThemePart(x => x.content.width);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [loadingText, setLoadingText] = React.useState<string>("loading data");
    //const [expertMode, setExpertMode] = React.useState<boolean>(() => plIsHidden || plHasCategories || anyNodeHasChanges);
    const [showExtraButtons, setShowExtraButtons] = React.useState<boolean>(true);
    const [hasChanges, setHasChanges] = React.useState<boolean>(false);
    const [hasTriggers, setHasTriggers] = React.useState<boolean>(false);

    React.useEffect(() => {
        setHasChanges(!_.isEqual(playlist, originalPlaylist) || !_.isEqual(roles, loadedRoles));
    }, [playlist, originalPlaylist, roles, loadedRoles]);

    //console.log("hasChanges", hasChanges, "playlist", playlist, "originalPlaylist", originalPlaylist);

    const isMobile = useBelowBreakpoint("tablet");
    const isSmallDevice = useBelowBreakpointOrEqual("tablet");

    const isSmallDesktop = contentWidth <= breakpoints.desktop.width;
    const plIsHidden = playlist?.hidden;
    const plHasCategories = (playlist?.categories ?? []).length > 0;
    const nodes: PlayListNodeDownloadDto[] = [];
    visit(playlist?.startNode, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));
    const anyNodeHasChanges = nodes.some(node =>
        (node.descriptions?.length ?? 0) > 0 ||
        node.color !== "@darkGrey" ||
        node.children?.some(child =>
            child.transitionFunction !== "FallThru" &&
            child.transitionFunction !== "PercentageDone"
        )
    );
    const setExpertMode = React.useCallback((expertMode: Updater<boolean>) => {
        pleDispatch(PlaylistEditActions.setExpertMode(expertMode));
    }, [pleDispatch]);


    // console.log("plist", playlist, "plIsHidden", plIsHidden, "plHasCategories", plHasCategories, "anyNodeHasChanges", anyNodeHasChanges, "expertMode", expertMode);
    // console.log("playlist", playlist);
    React.useEffect(() => {
        if (plIsHidden || plHasCategories || anyNodeHasChanges) {
            setExpertMode(true);
        }
    }, [plIsHidden, plHasCategories, anyNodeHasChanges, pleDispatch, setExpertMode]);


    const playlistId = playlist?.id;
    const playlistContents = React.useMemo(() => playlist?.contents ?? [], [playlist?.contents]);

    React.useEffect(() => {
        const load = async () => {
            if (!playlistContents || playlistContents.length === 0) {
                pleDispatch(PlaylistEditActions.setPosts([]));
            }
            else {
                const newPosts = _.filter(_.clone(posts), p => _.find(playlistContents, pc => pc.referenceId === p.id) !== undefined);
                const toLoad = _.filter(playlistContents, pc => _.find(posts, p => p.id === pc.referenceId) === undefined);
                const work = _.map(toLoad, async c => loadPostForPlaylist(c, playlistId));
                const res = await Promise.all(work);
                const toAdd = _.compact(res);
                if (toAdd.length > 0) {
                    newPosts.push(...toAdd);
                    pleDispatch(PlaylistEditActions.setPosts(newPosts));
                }
            }
            setLoading(false);
        }

        const loadRoles = async () => {
            if (playlistId) {
                const xxx = await ControllerHelper.singleCall({ playlistId: playlistId }, PlaylistController.GetPlaylistMembersWithRoles, true);
                const roles: RoleMap = _.map(xxx.elements, e => ({
                    user: _.find(xxx.data, d => d.id === e.userId)!,
                    roles: e.roles,
                    //#endregion
                }));
                setLoadedRoles(_.cloneDeep(roles));
                setRoles(roles);
            }
        };
        loadRoles();

        if (_.find(playlistContents, pc => _.find(posts, p => p.id === pc.referenceId) === undefined))
            load();
        else {
            if (!playlistContents || playlistContents.length === 0) {
                pleDispatch(PlaylistEditActions.setPosts([]));
            }
            setLoading(false);
        }
    }, [playlistContents, posts, playlistId, pleDispatch]);

    // const save = React.useMemo(() => async (save: boolean, close?: boolean, cancel?: boolean) => {
    const save = React.useMemo(() => async (save: boolean, goto?: "assign" | "overview") => {
        let curId = id;

        setLoadingText("saving playlist");
        setLoading(true);
        //console.log("loadedRoles", loadedRoles);
        //console.log("roles", roles);
        const rolesToSet = calcRolesToSet(loadedRoles ?? [], roles ?? [], copyPlaylist);
        //console.log("rolesToSet", rolesToSet);
        const actions: AnyAction[] = [Actions.setPlaylistEditMode(false)];
        if (save && playlist) {
            if (id && !copyPlaylist) {
                // const res =
                await ControllerHelper.singleCall(
                    {
                        id,
                        data: playlist,
                        media: (playlist.media as any).file,
                    }, PlaylistController.UpdatePlaylistCompletely, true);
                // console.log('res', res.startNode?.children && res.startNode?.children[0].to?.visible);
                console.log("roles", rolesToSet);
                await ControllerHelper.singleCall({ playlistId: id, assignments: _.map(rolesToSet, r => ({ userId: r.user.id, roles: r.roles })) }, PlaylistController.AssignPlaylistRolesToUsers, true);

            }
            else {
                const res = await ControllerHelper.singleCall({
                    groupid: playlist.group_id,
                    data: playlist,
                    media: (playlist.media as any).file,
                }, PlaylistController.AddPlaylist, true);
                curId = res?.id;
                await ControllerHelper.singleCall({ playlistId: curId, assignments: _.map(rolesToSet, r => ({ userId: r.user.id, roles: r.roles })) }, PlaylistController.AssignPlaylistRolesToUsers, true);
            }
        }
        dispatch(PluginActions.batchActions(actions));
        setLoading(false);

        pleDispatch(PlaylistEditActions.init(playlist));

        if (goto === "assign" && curId)
            gotoNamedRoute("playlist_assign", { id: curId as any, tab: "self" });

        else if (goto === "overview")
            gotoNamedRoute("playlist_manage");
        else if (curId !== id)
            gotoNamedRoute("playlist_edit", { id: curId });
    }, [playlist, dispatch, id, copyPlaylist, pleDispatch, roles, loadedRoles]);



    const tabsSelect = React.useMemo(() => (idx: number) => {
        setSelectedTab(PlaylistEditTabs[idx % PlaylistEditTabs.length]);
    }, []);


    const tabGeneralHasError = React.useMemo(() => {
        return _.some(missing.generalSettings, x => x === true);
    }, [missing]);

    const tabContentHasError = React.useMemo(() => {
        return _.some(missing.content, x => x === true);
    }, [missing]);

    const tabLinearHasError = React.useMemo(() => {
        return _.some(missing.phases, x => x === true);
    }, [missing]);

    const tabAutomationHasError = React.useMemo(() => {
        return _.some(missing.automation, x => x === true);
    }, [missing]);

    const tabAccessHasError = React.useMemo(() => {
        // if (_.some(missing.access, x => x === true)) return true;
        if (roles && roles.some(r => r.roles.length < 1)) return true;
        return false;
    }, [roles]);

    const hasAnyError = React.useMemo(() => {
        return tabGeneralHasError || tabContentHasError || tabLinearHasError || tabAutomationHasError || tabAccessHasError;
    }, [tabGeneralHasError, tabContentHasError, tabLinearHasError, tabAutomationHasError, tabAccessHasError]);

    // React.useEffect(() => {
    //     setHasChanges(true);
    // }, [playlist, originalData]);

    // React.useEffect(() => {
    //     dispatch(PluginActions.batchActions([
    //         Actions.setPlaylistEditMode(true),
    //         Actions.setPlaylistSave(save),
    //     ]));

    // }, [save, dispatch]);

    useCalcMissingForPlaylistEdit(playlist, missing);
    //console.log("playlist", playlist);

    if (!playlist) {
        return <div></div>;
    }

    const missingStr: string[] = [];

    if (missing.generalSettings.groupId)
        missingStr.push(t("<b>general:</b> group is missing"));
    if (missing.generalSettings.headline)
        missingStr.push(t("<b>general:</b> title is missing"));
    if (missing.generalSettings.media)
        missingStr.push(t("<b>general:</b> media is missing"));
    if (missing.generalSettings.description)
        missingStr.push(t("<b>general:</b> description is missing"));
    if (missing.content.contents)
        missingStr.push(t("<b>contents:</b> content is missing"));
    if (missing.phases.startNode)
        missingStr.push(t("<b>phases:</b> start node is missing"));
    if (roles && roles.some(r => r.roles.length < 1))
        missingStr.push(t("<b>access:</b> each member must have at least one role"));
    // if (plMode === "Linear")
    //     _.forEach(missing?.linear, m => { missingStr.push(t("error in phase: <b>{{node}}</b>", { node: m === "" ? "no title" : m })); });
    const toggleExpertMode = () => {
        console.log("toggleExpertMode", expertMode);
        setExpertMode(v => !v);
    }

    let hasMissing = missing === undefined || missingStr.length > 0;
    _.forEach(missing, (m, i) => {
        if (m === true) {
            hasMissing = true;
            return;
        }
        if (Array.isArray(m) && m.length > 0) {
            hasMissing = true;
            return;
        }
    });


    const userRoles = getPlaylistUserRoles(playlist as PlaylistDownloadDto);

    /*
     Erzeuge mir eine Playlist zum Thema Taycan. Mit dem Titel Taycan. Als Beschreibung einige rudimentären Informationen über den Taycan. Dafür möchte ich, dass du mir in den Wiki-Artikeln Artikel suchst, erstens zum Thema Taycan im Allgemeinen, dann zum Thema Rekuperation und dann zum Thema Laden zu Hause.
    
    */
    return (
        <>
            <Loader active={loading} size="small" light infoText={t(loadingText)} />
            {enablePlaylistAIMode &&
                <div style={{ position: "absolute", top: 2, right: 0, width: "5.56%", transform: "translateX(100%)" }}>
                    <PlaylistAIModal
                        setLoading={setLoading}
                        buttonProps={({
                            kind: "editButton",
                            content: isSmallDesktop ? undefined : ("AI"),
                            tooltip: { tooltipText: ("activate AI (Test mode)") },
                            fluid: true
                        })}

                    />
                </div>
            }
            <div style={{ marginBottom: -20 }} />
            <EditPartContainer
                icon={id ? "edit" : "add"}
                title={id ? t("edit playlist") : t("add new playlist")}
                subTitle={removeHtmlTags(getTranslated(playlist.headlines, contentLanguage)?.text ?? "")}
                bold
                relative
            >
                <>
                    <div style={{ position: "absolute", top: 0, right: 0 }}>
                        {selectedTab !== "Content" && showExtraButtons &&
                            <Tooltip tooltipText={expertMode ? t("deactivate expert mode") : ""} tooltipContent={expertMode ? undefined : <div style={{ textAlign: "left" }}><h4>{t("activate expert mode")}</h4>
                                <p>{t("in expert mode, more options that differ from the standard settings are displayed")}</p>
                            </div>} noMargin>
                                <ExpertSwitchContainer active={expertMode}>
                                    <SwitchButton isActive={toggleExpertMode} initialState={expertMode} activeColor="@veryLightGrey" activeDotColor='@accentBlue' />
                                    <ExpertSwitch onClick={toggleExpertMode}>
                                        {t("expert mode {{on_off}}", { on_off: expertMode ? "on" : "off" })}
                                    </ExpertSwitch>
                                </ExpertSwitchContainer>
                            </Tooltip>
                        }
                    </div>
                    <div style={{ marginTop: 20 }} />
                    <TabsBar
                        onChange={tabsSelect}
                        autoMode={true}
                        isSmallDevice={isSmallDevice}
                        input={[
                            {
                                title: t("general settings"),
                                icon: "edit",
                                active: selectedTab === "General",
                                checked: !tabGeneralHasError,
                                msg: !tabGeneralHasError ? undefined : t("missing input")
                            },
                            {
                                title: t("contents"),
                                icon: "file",
                                active: selectedTab === "Content",
                                checked: !tabContentHasError,
                                msg: !tabContentHasError ? undefined : t("missing input")
                            },
                            {
                                // title: t("sequence mode {{mode}}", { mode: plMode?.toString() }),
                                title: t("phases"),
                                icon: "editor list bullet",
                                active: selectedTab === "Sequence",
                                hidden: tabContentHasError,
                                checked: !tabLinearHasError,
                                // missingStr.length > 0
                                msg: !(tabLinearHasError) ? undefined : t("missing input")
                            },
                            {
                                // title: t("sequence mode {{mode}}", { mode: plMode?.toString() }),
                                title: t("automation"),
                                icon: "cog",
                                active: selectedTab === "Automation",
                                hidden: !config.showPlaylistAutomation && tabLinearHasError,
                                checked: hasTriggers && tabAutomationHasError === false,
                                // // missingStr.length > 0
                                msg: undefined,
                            },
                            {
                                // title: t("sequence mode {{mode}}", { mode: plMode?.toString() }),
                                title: t("access"),
                                icon: "login",
                                active: selectedTab === "Access",
                                hidden: tabLinearHasError ? true : !may(playlist as PlaylistDownloadDto, "assignRoles"),
                                // checked: !tabAccessHasError,
                                checked: roles && roles.length > 0 && !tabAccessHasError,
                                msg: !tabAccessHasError ? undefined : t("missing input")
                            },
                            {
                                // title: t("sequence mode {{mode}}", { mode: plMode?.toString() }),
                                title: t("trainer content"),
                                // title: "",
                                icon: "education",
                                active: selectedTab === "TrainerContent",
                                hidden: tabContentHasError,
                                checked: !tabLinearHasError,
                                // missingStr.length > 0
                                msg: !(tabLinearHasError) ? undefined : t("missing input")
                            },
                        ]}
                        additionalContent={
                            <>

                                {(hasMissing || hasAnyError) ?
                                    <Button
                                        kind="halfTransparentButton"
                                        icon="exclamation triangle"
                                        floated='right'
                                        iconColor='@accentRed'
                                        indexColor='@accentRed'
                                        index={hasAnyError ? undefined : missingStr.length}

                                        disabled
                                        cursor='normal'
                                        tooltip={{
                                            tooltipText: "",
                                            tooltipContent: <>
                                                <MissingElementAlternativeTitle>
                                                    <Icon name="exclamation triangle" style={{ float: "left", marginRight: 5 }} marginTop={6} size={22} color='@accentRed' />
                                                    {t("save disabled")}
                                                </MissingElementAlternativeTitle>
                                                {missingStr.length > 0 ? _.map(missingStr, (ms, i) => (
                                                    <MissingElementAlternative key={i} style={{ textAlign: "left" }}>
                                                        <div style={{ display: "grid", gridTemplateColumns: "20px 1fr", columnGap: 7, marginLeft: 0 }}>
                                                            <Icon name="arrow right" style={{ float: "right", paddingBottom: 20 }} marginTop={2} size={13} />
                                                            <SafeHTML allowedTags={["b"]} allowedAttributes={{}} html={ms} />
                                                        </div>
                                                    </MissingElementAlternative>
                                                )) :
                                                    t("missing input")
                                                }
                                            </>
                                        }}
                                    />
                                    :
                                    <>
                                        {showExtraButtons &&
                                            <>
                                                {saveRoutineSingle ?
                                                    <>
                                                        <Button
                                                            // kind="primary"
                                                            floated='right'
                                                            kind={hasAnyError ? "cancelButton" : (hasChanges ? "primary" : "halfTransparentButton")}
                                                            icon='save'
                                                            content={isSmallDesktop ? undefined : t("save and exit")}
                                                            onClick={() => save(true, "overview")}
                                                            tooltip={{ tooltipText: t("save and close playlist edit") }}
                                                            disabled={hasAnyError}
                                                            cursor={hasAnyError ? "not-allowed" : "pointer"}
                                                        />
                                                        <Button
                                                            kind={hasAnyError ? "cancelButton" : "halfTransparentButton"}
                                                            floated='right'
                                                            content={isSmallDesktop ? undefined : t("save")}
                                                            icon='check'
                                                            onClick={() => save(true)}
                                                            tooltip={{ tooltipText: t("apply changes") }}
                                                            disabled={hasAnyError}
                                                            cursor={hasAnyError ? "not-allowed" : "pointer"}
                                                        />
                                                        {userRoles.assign &&
                                                            <Button
                                                                kind={hasAnyError ? "cancelButton" : "halfTransparentButton"}
                                                                floated='right'
                                                                icon='user plus'
                                                                content={isSmallDesktop ? undefined : t("save and assign")}
                                                                onClick={() => save(true, "assign")}
                                                                tooltip={{ tooltipText: t("save and assign playlist") }}
                                                                disabled={hasAnyError}
                                                                cursor={hasAnyError ? "not-allowed" : "pointer"}
                                                            />
                                                        }
                                                    </>
                                                    :
                                                    <MoreButton
                                                        ele={playlist}
                                                        floated='right'
                                                        icon='save'
                                                        content={"save options"}
                                                        kind='primary'
                                                        hideMoreButton
                                                        entries={
                                                            [
                                                                {
                                                                    onClick: () => save(true, "overview"),
                                                                    icon: "save",
                                                                    content: t("save and exit"),
                                                                    tooltip: { tooltipText: t("save and close playlist edit"), position: "bottom" }

                                                                },
                                                                {
                                                                    icon: 'user plus',
                                                                    content: t("save and assign"),
                                                                    onClick: () => save(true, "assign"),
                                                                    tooltip: { tooltipText: t("save and assign playlist edit"), position: "bottom" },
                                                                    disabled: !isEdit || !userRoles.assign
                                                                },
                                                                {
                                                                    content: t("save"),
                                                                    icon: 'check',
                                                                    onClick: () => save(true),
                                                                    tooltip: { tooltipText: t("apply changes"), position: "bottom" },
                                                                    disabled: !isEdit
                                                                }
                                                            ]
                                                        }
                                                    />
                                                }
                                            </>
                                        }
                                    </>
                                }
                                <div onClick={() => setShowExtraButtons(!showExtraButtons)}>
                                    <CancelWithConfirmation
                                        cancel={() => save(false, "overview")}
                                        floated='right'
                                        confirmText={id ? t("yes, cancel edit playlist") : t("yes, cancel add playlist")}
                                        kind={hasChanges ? 'halfTransparentButton' : "secondary"}
                                        tooltipInitialButton={t("cancel")}
                                        iconOnly
                                    />
                                </div>
                            </>
                        }
                    />
                    <InputContainer isMobile={isMobile}>
                        {selectedTab === "General" &&
                            <PlaylistEditGeneral />
                        }
                        {selectedTab === "Content" &&
                            <PlaylistEditContent />
                        }
                        {selectedTab === "Sequence" &&
                            <PlaylistEditSequenceLinear />
                        }
                        {selectedTab === "Automation" && config.showPlaylistAutomation &&
                            <PlaylistEditAutomation hasTriggersCallBack={(v) => setHasTriggers(v)} />
                        }
                        {selectedTab === "Access" &&
                            <PlaylistEditAccess
                                roles={roles}
                                setRoles={setRoles}
                            />
                        }
                        {selectedTab === "TrainerContent" &&
                            <PlaylistEditTrainerContent />
                        }
                    </InputContainer>
                </>
            </EditPartContainer>
            {isGlobalAdmin && selectedTab !== "Content" && !hasMissing &&
                <PlaylistSC.AdminPanel>
                    <div style={{ float: "left", lineHeight: "40px", paddingLeft: 20 }}>Temporary Admin Panel</div>
                    <div style={{ float: "right", borderLeft: "1px solid #e9e9e9", width: 1, height: "100%", marginRight: 10 }} />
                    <div style={{ float: "right", lineHeight: "20px", marginRight: 10, marginTop: 9, cursor: "pointer" }} onClick={() => setSaveRoutineSingle(!saveRoutineSingle)}>
                        Save View
                    </div>
                    <div style={{ float: "right", lineHeight: "20px", marginRight: 10, marginTop: 7 }}>
                        <Checkbox label='More Button' onClick={() => !saveRoutineSingle ? undefined : setSaveRoutineSingle(false)} selected={!saveRoutineSingle} />
                    </div>
                    <div style={{ float: "right", lineHeight: "20px", marginRight: 10, marginTop: 7 }}>
                        <Checkbox label='Single Buttons' onClick={() => saveRoutineSingle ? undefined : setSaveRoutineSingle(true)} selected={saveRoutineSingle} />
                    </div>
                    <div style={{ float: "right", borderLeft: "1px solid #e9e9e9", width: 1, height: "100%", marginRight: 10 }} />
                </PlaylistSC.AdminPanel>
            }

        </>
    )
}

const PlaylistEditMain = (p: PLaylistEditHomeProps) => {
    const { routeParams } = useAppSelector(mapper, shallowCompare);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const id = routeParams?.id as string;

    const [data, setData] = React.useState<PlaylistUploadDto>({
        categories: [],
        headlines: [],
        descriptions: [],
        group_id: "",
        media: {} as MediaDownloadDto,
        tags: [],
        languageTags: [],
        stickyHours: 0,
        sequencingMode: "Linear",
        trainerDocumentsReferenceIds: []
    });



    React.useEffect(() => {
        const load = async () => {
            if (id) {
                const playlist = await ControllerHelper.singleCall({ id }, PostController.GetSinglePost) as any as PlaylistDownloadDto;
                //console.log("loaded playlist", playlist);
                setData(playlist);
                setIsLoading(false);
            }
            else {
                setData({
                    categories: [],
                    headlines: [],
                    descriptions: [],
                    group_id: "",
                    media: {} as MediaDownloadDto,
                    tags: [],
                    languageTags: [],
                    stickyHours: 0,
                    sequencingMode: "Linear",
                    trainerDocumentsReferenceIds: []
                });
                setIsLoading(false);
            }
        }
        load();
    }, [id]);
    return (
        <PlaylistEdit playlist={data}>
            {isLoading ? <LoaderInline active /> : <PlaylistEditHomeInternal {...p} />}
        </PlaylistEdit>
    );
}
// PlaylistEditHome.whyDidYouRender = true;
// PlaylistEditHomeInternal.whyDidYouRender = true;
export default PlaylistEditMain;

